import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Target, X } from 'lucide-react';
import { ernaehrungAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';

/* E1 Ziele & Zugangsstufen (15.10.2026): Karte auf Ernährung › Mitglied. Profile (Trainingstag, Ruhetag, Refeed) mit kcal
   und Makros, je Wochentag ein Modus, Datums-Ausnahmen, Zugangsstufe und zwei Schalter. Das Tagesziel rechnet der Server
   (ernaehrung/ziele.py): Ausnahme > Wochentag > Rechner > Bedarf — die Woche unten zeigt das Ergebnis. */

const TAGE = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const fehlertext = (e, rest = 'Das hat nicht geklappt') => (typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : rest);
const tagDe = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
const zahl = (v) => (v === '' || v === null || v === undefined ? null : Number(String(v).replace(',', '.')));
const feld = 'h-8 rounded-md border border-input bg-background px-2 text-sm';
const MODUS_CLS = { trainingstag: 'bg-emerald-500/10 text-emerald-700 border-emerald-200', ruhetag: 'bg-sky-500/10 text-sky-700 border-sky-200',
  refeed: 'bg-amber-500/10 text-amber-700 border-amber-200', pausiert: 'bg-muted text-muted-foreground', standard: '' };

function ProfilZeile({ name, label, wert, onChange }) {
  const an = !!wert;
  const w = wert || { kcal: '', eiweiss: '', kh: '', fett: '' };
  return (
    <div className="grid grid-cols-[7rem_1fr] items-center gap-2">
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={an} onChange={(e) => onChange(e.target.checked ? { kcal: '', eiweiss: '', kh: '', fett: '' } : null)} aria-label={`${label} verwenden`} />{label}
      </label>
      <div className="grid grid-cols-4 gap-1.5">
        {[['kcal', 'kcal'], ['eiweiss', 'P g'], ['kh', 'K g'], ['fett', 'F g']].map(([k, ph]) => (
          <Input key={k} inputMode="decimal" placeholder={ph} disabled={!an} value={w[k] ?? ''} onChange={(e) => onChange({ ...w, [k]: e.target.value })} className="h-8 text-sm" aria-label={`${label} ${ph}`} />
        ))}
      </div>
    </div>
  );
}

export function ZieleKarte({ leadId, darfBearbeiten = true }) {
  const [d, setD] = useState(null);
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  const [neuAus, setNeuAus] = useState({ datum: '', modus: 'pausiert', hinweis: '' });
  const laden = useCallback(() => ernaehrungAPI.ziele(leadId).then((r) => {
    setD(r.data);
    setF({ profile: r.data.profile, wochentage: r.data.wochentage, ausnahmen: r.data.ausnahmen, zugangsstufe: r.data.zugangsstufe,
      zahlen_verbergen: r.data.zahlen_verbergen, rechner_erlaubt: r.data.rechner_erlaubt, trainingstag_automatisch: r.data.trainingstag_automatisch,
      schrittziel: r.data.schrittziel ?? '' });
  }).catch(() => {}), [leadId]);
  useEffect(() => { laden(); }, [laden]);
  if (!d || !f) return null;

  const speichern = async (teil) => {
    setBusy(true);
    try {
      const { data } = await ernaehrungAPI.zieleSpeichern(leadId, teil);
      setD(data);
      setF({ profile: data.profile, wochentage: data.wochentage, ausnahmen: data.ausnahmen, zugangsstufe: data.zugangsstufe,
        zahlen_verbergen: data.zahlen_verbergen, rechner_erlaubt: data.rechner_erlaubt, trainingstag_automatisch: data.trainingstag_automatisch,
        schrittziel: data.schrittziel ?? '' });
      toast.success('Gespeichert');
    } catch (e) { toast.error(fehlertext(e)); } finally { setBusy(false); }
  };
  const profileSpeichern = () => {
    const profile = Object.fromEntries(Object.entries(f.profile).map(([k, v]) => [k, v ? { kcal: zahl(v.kcal), eiweiss: zahl(v.eiweiss) ?? 0, kh: zahl(v.kh) ?? 0, fett: zahl(v.fett) ?? 0 } : null]));
    speichern({ profile, wochentage: f.wochentage });
  };
  const ausnahmeDazu = () => {
    if (!neuAus.datum) { toast.error('Datum fehlt'); return; }
    speichern({ ausnahmen: [...f.ausnahmen.filter((a) => a.datum !== neuAus.datum), { datum: neuAus.datum, modus: neuAus.modus, hinweis: neuAus.hinweis || null }] });
    setNeuAus({ datum: '', modus: 'pausiert', hinweis: '' });
  };
  const modusName = (m) => (d.ausnahme_modi.find((x) => x.wert === m) || {}).name || m;
  // E9: Schrittziel je Kunde — leer = Standard des Studios; gilt in Tagebuch, Heute-Bildschirm und Schritte-Chart
  const schrittzielSpeichern = () => {
    const z = zahl(f.schrittziel);
    if (z !== null && !Number.isInteger(z)) { toast.error('Schrittziel: ganze Zahl'); return; }
    speichern({ schrittziel: z });
  };

  return (
    <Card data-testid="ziele-karte">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2"><Target className="w-4 h-4" /> Ziele & Zugang
          <Badge variant="outline">{d.zugang.stufe_name}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        {/* Heute + Woche: das gerechnete Ergebnis, EINE Regel auf dem Server */}
        <div>
          <p className="text-xs font-medium mb-1">Diese Woche</p>
          <div className="grid grid-cols-7 gap-1">
            {d.woche.map((t, i) => (
              <div key={t.datum} className={`rounded-md border p-1.5 text-center ${t.datum === d.heute.datum ? 'border-primary' : 'border-border/60'}`} title={t.hinweis || ''}>
                <div className="text-[10px] text-muted-foreground">{TAGE[i]} {tagDe(t.datum)}</div>
                <div className="text-sm font-semibold tabular-nums">{t.pausiert ? '–' : (t.kcal ?? '?')}</div>
                <Badge variant="outline" className={`text-[9px] px-1 py-0 ${MODUS_CLS[t.modus] || ''}`}>{modusName(t.modus)}{t.quelle === 'ausnahme' ? ' *' : ''}{t.quelle === 'rechner' ? ' (Rechner)' : ''}</Badge>
              </div>
            ))}
          </div>
          {d.heute?.hinweis && <p className="text-xs text-muted-foreground mt-1">{d.heute.hinweis}</p>}
        </div>

        {/* Profile + Wochentage */}
        <div className="space-y-2">
          <p className="text-xs font-medium">Tagesprofile <span className="text-muted-foreground font-normal">(leer = Standard aus dem Kundenprofil)</span></p>
          {[['trainingstag', 'Trainingstag'], ['ruhetag', 'Ruhetag'], ['refeed', 'Refeed']].map(([k, label]) => (
            <ProfilZeile key={k} name={k} label={label} wert={f.profile[k]} onChange={(v) => setF({ ...f, profile: { ...f.profile, [k]: v } })} />
          ))}
          <div className="grid grid-cols-7 gap-1">
            {TAGE.map((t, i) => (
              <label key={t} className="text-center text-[10px] text-muted-foreground">{t}
                <select value={f.wochentage[String(i)]} onChange={(e) => setF({ ...f, wochentage: { ...f.wochentage, [String(i)]: e.target.value } })} className={`${feld} w-full mt-0.5 text-xs px-1`} aria-label={`Modus ${t}`} disabled={!darfBearbeiten}>
                  {d.modi.map((m) => <option key={m.wert} value={m.wert} disabled={m.wert !== 'standard' && !f.profile[m.wert]}>{m.name}</option>)}
                </select>
              </label>
            ))}
          </div>
          {darfBearbeiten && <Button size="sm" disabled={busy} onClick={profileSpeichern}>{busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Profile & Wochentage speichern</Button>}
        </div>

        {/* Ausnahmen */}
        <div className="space-y-1.5">
          <p className="text-xs font-medium">Ausnahmen <span className="text-muted-foreground font-normal">(gehen vor dem Wochentag, z. B. 24.12. pausiert)</span></p>
          {f.ausnahmen.length === 0 && <p className="text-xs text-muted-foreground">keine</p>}
          {f.ausnahmen.map((a) => (
            <div key={a.datum} className="flex items-center gap-2 text-xs">
              <span className="tabular-nums">{new Date(`${a.datum}T12:00:00`).toLocaleDateString('de-DE')}</span>
              <Badge variant="outline" className={MODUS_CLS[a.modus] || ''}>{modusName(a.modus)}</Badge>
              {a.hinweis && <span className="text-muted-foreground">{a.hinweis}</span>}
              {darfBearbeiten && <button type="button" aria-label="Ausnahme entfernen" onClick={() => speichern({ ausnahmen: f.ausnahmen.filter((x) => x.datum !== a.datum) })}><X className="w-3 h-3" /></button>}
            </div>
          ))}
          {darfBearbeiten && (
            <div className="flex flex-wrap items-center gap-1.5">
              <Input type="date" value={neuAus.datum} onChange={(e) => setNeuAus({ ...neuAus, datum: e.target.value })} className="h-8 w-40 text-sm" aria-label="Datum der Ausnahme" />
              <select value={neuAus.modus} onChange={(e) => setNeuAus({ ...neuAus, modus: e.target.value })} className={feld} aria-label="Modus der Ausnahme">
                {d.ausnahme_modi.map((m) => <option key={m.wert} value={m.wert} disabled={['trainingstag', 'ruhetag', 'refeed'].includes(m.wert) && !f.profile[m.wert]}>{m.name}</option>)}
              </select>
              <Input value={neuAus.hinweis} onChange={(e) => setNeuAus({ ...neuAus, hinweis: e.target.value })} placeholder="Hinweis (optional)" maxLength={120} className="h-8 w-44 text-sm" />
              <Button size="sm" variant="outline" className="h-8" disabled={busy} onClick={ausnahmeDazu}>Hinzufügen</Button>
            </div>
          )}
        </div>

        {/* Zugang */}
        <div className="space-y-2 rounded-md border p-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium">Zugangsstufe</span>
            <select value={f.zugangsstufe} disabled={!darfBearbeiten || busy} onChange={(e) => speichern({ zugangsstufe: e.target.value })} className={feld} aria-label="Zugangsstufe">
              {d.stufen.map((s) => <option key={s.wert} value={s.wert}>{s.name}</option>)}
            </select>
            <span className="text-xs text-muted-foreground">
              {{ voll: 'alles', standard: 'alles außer eigener Rezeptbauer', nur_foto: 'Fototagebuch + Schnell-Eintrag, keine Suche', nur_plan: 'Plan ansehen, kein Logging',
                wettkampf: 'alles; Tagesziel aus der Wettkampf-Phase (nur Leitung, nur mit Wettkampf-Modus)' }[f.zugangsstufe]}
            </span>
          </div>
          <label className="flex items-center gap-2 text-sm"><Switch checked={f.zahlen_verbergen} disabled={!darfBearbeiten || busy} onCheckedChange={(v) => speichern({ zahlen_verbergen: v })} aria-label="Zahlen verbergen" /> Zahlen verbergen <span className="text-xs text-muted-foreground">(kcal und Makros in der App aus, Logging bleibt)</span></label>
          <label className="flex items-center gap-2 text-sm"><Switch checked={f.rechner_erlaubt} disabled={!darfBearbeiten || busy} onCheckedChange={(v) => speichern({ rechner_erlaubt: v })} aria-label="Kalorienrechner erlauben" /> Kalorienrechner in der App darf Ziele speichern</label>
          {/* E11: Trainingstag aus dem Studio-Check-in — braucht das Profil Trainingstag; der Server liest Check-in und Buchung */}
          <label className="flex items-center gap-2 text-sm"><Switch checked={!!f.trainingstag_automatisch} disabled={!darfBearbeiten || busy || !f.profile?.trainingstag} onCheckedChange={(v) => speichern({ trainingstag_automatisch: v })} aria-label="Trainingstag automatisch" /> Trainingstag automatisch aus Studio-Check-in oder Kursbuchung <span className="text-xs text-muted-foreground">{f.profile?.trainingstag ? '(Profil Trainingstag gilt an Tagen mit Check-in oder gebuchtem Kurs)' : '(erst das Profil Trainingstag anlegen)'}</span></label>
          <div className="flex flex-wrap items-center gap-2">
            <label htmlFor={`schrittziel-${leadId}`} className="text-xs font-medium">Schrittziel je Tag</label>
            <Input id={`schrittziel-${leadId}`} inputMode="numeric" value={f.schrittziel ?? ''} onChange={(e) => setF({ ...f, schrittziel: e.target.value })}
              placeholder={`Standard ${d.schrittziel_standard?.toLocaleString('de-DE') || ''}`} disabled={!darfBearbeiten || busy} className="h-8 w-32 text-sm" />
            {darfBearbeiten && <Button size="sm" variant="outline" className="h-8" disabled={busy || String(f.schrittziel ?? '') === String(d.schrittziel ?? '')} onClick={schrittzielSpeichern}>Übernehmen</Button>}
            <span className="text-xs text-muted-foreground">leer = Standard · gilt für Tagebuch, Heute-Bildschirm und Apple Health</span>
          </div>
          {d.rechner?.ziel && (
            <p className="text-xs text-muted-foreground">Rechner-Ziel des Mitglieds: {d.rechner.ziel.kcal} kcal · P {d.rechner.ziel.eiweiss} · K {d.rechner.ziel.kh} · F {d.rechner.ziel.fett}{f.rechner_erlaubt ? ' — gilt als Standard' : ' — gilt nicht (Rechner aus)'}
              {darfBearbeiten && <button type="button" className="ml-2 underline" onClick={() => speichern({ rechner: null })}>löschen</button>}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
